import { renderAttrValue } from '../components/ArticleComponents/ItemDataCategoryTable/ItemDataCategoryTableUtils';
import isUndefined from 'lodash/isUndefined';

/**
 * Normalizza testo dalle API convertendo caratteri Unicode escape
 *
 * @param {string} text - Il testo da normalizzare
 * @returns {string} Il testo normalizzato
 */
export const normalizeUnicodeText = (text) => {
    if (!text || typeof text !== 'string') {
        return '';
    }

    try {
        // Metodo principale: JSON.parse gestisce tutti gli escape
        return JSON.parse('"' + text.replace(/"/g, '\\"') + '"');
    } catch (e) {
        // Fallback: conversione manuale
        return text
            .replace(/\\u([0-9a-fA-F]{4})/g, (match, code) =>
                String.fromCharCode(parseInt(code, 16))
            )
            .replace(/\\n/g, '\n')
            .replace(/\\t/g, '\t')
            .replace(/\\r/g, '\r')
            .replace(/\\\\/g, '\\')
            .replace(/\\"/g, '"');
    }
};

/**
 * Converte \n in <br> per uso con dangerouslySetInnerHTML
 *
 * @param {string} text - Il testo con caratteri \n
 * @returns {string} Il testo con <br> al posto di \n
 */

export const convertLineBreaksToHtml = (text) => {
    return text ? text.replace(/\n/g, '<br>') : '';
};

/**
 * Rileva se il testo contiene tag HTML
 *
 * @param {string} text - Il testo da analizzare
 * @returns {boolean} True se contiene HTML, false altrimenti
 */
export const containsHtml = (text) => {
    if (!text || typeof text !== 'string') return false;
    return /<[^>]*>/.test(text);
};

/**
 * Funzione completa che normalizza e prepara il testo per il rendering
 *
 * @param {string} text - Il testo da preparare
 * @param {Object} options - Opzioni di configurazione
 * @param {boolean|null} options.forceHtml - Forza il trattamento come HTML
 * @returns {{text: string, isHtml: boolean}} Oggetto con testo preparato e flag HTML
 */
export const prepareApiTextForRender = (text, options = {}) => {
    const { forceHtml = null } = options;

    if (!text || typeof text !== 'string') {
        return { text: '', isHtml: false };
    }

    // Step 1: Normalizza Unicode
    const normalizedText = normalizeUnicodeText(text);

    // Step 2: Determina se deve essere trattato come HTML
    const hasHtml = forceHtml !== null ? forceHtml : containsHtml(normalizedText);

    // Step 3: Se è HTML, converti \n in <br>
    const finalText = hasHtml
        ? convertLineBreaksToHtml(normalizedText)
        : normalizedText;

    return {
        text: finalText,
        isHtml: hasHtml
    };
};

/**
 * Helper per processare il valore completo dell'attributo
 *
 * @param {Object} attr - Oggetto attributo con key e value
 * @param {Object} options - Opzioni per prepareApiTextForRender
 * @returns {{text: string, isHtml: boolean}} Oggetto con testo preparato e flag HTML
 */
export const processAttrValueForRender = (attr, options = {}) => {
    const rawValue = renderAttrValue(attr);

    if (rawValue === null || isUndefined(rawValue) || rawValue === '') {
        return { text: '', isHtml: false };
    }

    return prepareApiTextForRender(rawValue, options);
};